import { useEffect, useState } from 'react';
import { ArrowRight, Check, Download, MessageCircle, QrCode, ShieldCheck, Smartphone, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { SectionHeading } from './SectionHeading';

const MOMO_PROVIDERS = ['MTN Mobile Money', 'Telecel Cash', 'AirtelTigo Money'];

const INSTALL_STEPS = [
  { icon: Smartphone, title: 'Open your link on Android (or iPhone)', body: 'Tap the staff app link your pharmacy owner shares — most teams use an Android phone, and it works the same way on iPhone.' },
  { icon: Download, title: 'Tap "Add to Home Screen"', body: 'Your phone\u2019s browser offers this automatically — no app store, no download size to worry about on a slow connection.' },
  { icon: QrCode, title: 'Open it like any other app', body: 'It now sits on your home screen with your pharmacy\u2019s own icon — leave requests, expiry alerts and notifications, right there.' },
];

export const GhanaSection = () => {
  const [plans, setPlans] = useState({ essentials: 350, professional: 1200 });

  useEffect(() => {
    api.get('/billing/plans').then((res) => {
      const list = res.data?.plans;
      if (Array.isArray(list)) {
        const map = {};
        for (const p of list) if (p.key) map[p.key] = p.monthly_ghs;
        setPlans((prev) => ({ essentials: map.essentials ?? prev.essentials, professional: map.professional ?? prev.professional }));
      }
    }).catch(() => {});
  }, []);

  const waMessage = encodeURIComponent('Hi DutiLyne! I\u2019d like to know more about pricing for my pharmacy.');

  return (
    <section className="wl-section wl-ghana-section" id="ghana" aria-labelledby="ghana-heading" data-testid="welcome-ghana-section">
      <div className="wl-wrap">
        <div className="wl-heading-row">
          <SectionHeading id="ghana-heading" eyebrow="AKWAABA — BUILT FOR GHANAIAN PHARMACIES" title={<>Priced in cedis.<br />Paid by Mobile Money.<br /><span>Ready for your team’s phones.</span></>}>
            DutiLyne already runs the way Ghanaian pharmacies pay, share and get set up — no card required, no app store, no long sign-up calls.
          </SectionHeading>
          <div className="wl-ghana-emblem" aria-hidden="true"><Wallet /><span>GHS PRICING<br />MOMO READY</span></div>
        </div>

        <div className="wl-ghana-momo" data-testid="welcome-ghana-momo-row">
          {MOMO_PROVIDERS.map((p) => <span key={p} data-testid={`welcome-momo-${p.replace(/\s+/g, '-').toLowerCase()}`}><Check size={14} /> {p}</span>)}
          <span data-testid="welcome-momo-card"><Check size={14} /> Card payments too</span>
        </div>

        <div className="wl-ghana-grid">
          <div className="wl-ghana-pricing" data-testid="welcome-ghana-pricing">
            {[['essentials', 'Essentials', 'Independent, single-location pharmacies'], ['professional', 'Professional', 'Growing teams and branches']].map(([key, name, note]) => (
              <div key={key} className="wl-ghana-price-card" data-testid={`welcome-ghana-price-${key}`}>
                <strong>{name}</strong>
                <span className="wl-ghana-price-note">{note}</span>
                <span className="wl-ghana-price-amount">GHS {plans[key].toLocaleString()}<small>/month</small></span>
                <Link to={`/signup/pharmacy?plan=${key}&currency=ghs`} className="wl-text-link" data-testid={`welcome-ghana-start-${key}`}>Start free trial <ArrowRight size={15} /></Link>
              </div>
            ))}
            <p className="wl-small wl-ghana-trial-note" data-testid="welcome-ghana-trial-note">14-day free trial, no card needed — 21 days if a pharmacy refers you. Refer another pharmacy and earn 30 free days once they pay.</p>
          </div>

          <div className="wl-ghana-install" data-testid="welcome-ghana-install">
            <h3>Android-first. iPhone welcome too.</h3>
            <ol>
              {INSTALL_STEPS.map(({ icon: Icon, title, body }, i) => (
                <li key={title} data-testid={`welcome-ghana-install-step-${i}`}>
                  <span className="wl-ghana-install-number"><Icon size={18} /></span>
                  <div><strong>{title}</strong><p>{body}</p></div>
                </li>
              ))}
            </ol>
            <div className="wl-ghana-install-note"><ShieldCheck size={16} /><span>Your pharmacy owner sends this link once your workspace is set up — it already carries your pharmacy’s own name and logo.</span></div>
          </div>
        </div>

        <div className="wl-ghana-whatsapp-cta" data-testid="welcome-ghana-whatsapp-cta">
          <div><strong>Prefer to just ask on WhatsApp?</strong><span>Pricing, Mobile Money, demos — our team replies fast, in plain English.</span></div>
          <a href={`https://wa.me/233242160467?text=${waMessage}`} target="_blank" rel="noreferrer" className="wl-button wl-button-primary wl-ghana-whatsapp-btn" data-testid="welcome-ghana-whatsapp-link">
            <MessageCircle size={16} /> Chat with us on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};
